import { useMemo } from 'react'
import { Stack, Typography, Box } from '@vapor/material'
import { useTranslation } from 'react-i18next'
import { ReservationCartProps } from '../../form/reservation-new.type'
import { ReservationCartItem } from './reservation-cart-item'
import { calculateAdditionalServicePrice } from 'src/utils/additional-services-utils'
import { useTenant } from 'src/app/hooks/context/context.hook'
import ResettableTextField from '../resettable-text-field'
import * as R from 'ramda'

type ReservationCartNewProps = ReservationCartProps

export const ReservationCartNew = (props: ReservationCartNewProps) => {
  const { t } = useTranslation()
  const { currency } = useTenant()

  //#region Footer
  const totalAdditionalServices = useMemo(() => {
    return props.rows
      .map(
        (r) =>
          r.addedAdditionalServices
            ?.map((as) => {
              return calculateAdditionalServicePrice({
                adults: r.occupancy.adults,
                children: r.occupancy.children,
                price: as.price,
                days: r.nights,
                pricingType: as.pricingType,
                automaticForAdults: as.automaticForAdults,
                automaticForChildren: as.automaticForChildren,
                quantity: as.quantity,
                childPrices: as.childPrices,
                priceZero: as.priceZero,
              })
            })
            .reduce((prev, next) => prev + next, 0) ?? 0,
      )
      .reduce((prev, next) => prev + next, 0)
  }, [JSON.stringify(props.rows), currency])

  const initialTotalPrice = useMemo(() => {
    return (props.rows.length > 0 ? props.rows.map((row) => row.initialTotalPrice).reduce((acc, val) => acc + val, 0) : 0) + totalAdditionalServices
  }, [JSON.stringify(props.rows), totalAdditionalServices])

  const totalPrice = useMemo(() => {
    return (props.rows.length > 0 ? props.rows.map((row) => row.totalPrice).reduce((acc, val) => acc + val, 0) : 0) + totalAdditionalServices
  }, [JSON.stringify(props.rows), totalAdditionalServices])

  //#endregion
  return (
    <Stack width="calc(100% - 16px)">
      {props.rows.map((item, index) => (
        <ReservationCartItem
          key={`cartItem_${index}` + item.id}
          item={item}
          isEditable={props.isEditable}
          onUpdateRow={props.onUpdateRow}
          onDeleteRow={props.onDeleteRow}
          propertyId={props.propertyId}
        />
      ))}
      {!(props.rows.length > 0) ? (
        <Stack m={1} p={1} sx={{ backgroundColor: 'white' }} width="calc(100% - 16px)">
          <Typography variant="body">{t('pms.booking.reservation_new.cart_empty')}</Typography>
        </Stack>
      ) : (
        <>
          <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center" mb={1}>
            <Typography variant="body">
              <b>{t('pms.booking.reservation_new.cart_header_total_price')}</b>
            </Typography>
            <Box maxWidth="150px">
              <ResettableTextField
                changeOnBlur
                disabled={props.rows.length === 0 || !props.isEditable}
                initialValue={initialTotalPrice}
                min={totalAdditionalServices}
                value={totalPrice}
                onChange={(value) => {
                  if (value !== totalPrice && value !== null && props.onUpdateRow) {
                    const totalDiff = value - totalPrice
                    const isReset = value === initialTotalPrice

                    const updatedRows = props.rows.map((row) => {
                      const priceDiff = Math.round((row.totalPrice / (totalPrice - totalAdditionalServices)) * totalDiff)
                      const newTotal = row.totalPrice + priceDiff

                      return {
                        ...row,
                        totalPrice: isReset ? row.initialTotalPrice : newTotal,
                        averagePrice: isReset ? row.initialPrice : Math.round(newTotal / row.nights),
                      }
                    })

                    const firstRowsNewPrice = R.init(updatedRows).reduce((prev, curr) => prev + curr.totalPrice, 0)
                    const lastRowElement = R.last(updatedRows)

                    if (lastRowElement) {
                      const newTotal = value - firstRowsNewPrice

                      const lastItemUpdated = { ...lastRowElement, totalPrice: newTotal, averagePrice: Math.round(newTotal / lastRowElement.nights) }
                      props.onUpdateRow(R.update(-1, lastItemUpdated, updatedRows))
                    } else {
                      props.onUpdateRow(updatedRows)
                    }
                  }
                }}
                onResetCalled={props.onResetRowsPrices}
                resetPosition={'LEFT'}
              />
            </Box>
          </Stack>
        </>
      )}
    </Stack>
  )
}
